import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { updateBase, updateAdminBase, getBaseById, uploadImage } from '../services/api';
import { Modal, Heading, Text } from './ui';

const SAMPLE_IMAGE_URLS = [
  '',
  'https://images.unsplash.com/photo-1640421951187-c5e6e3507e9a',
  'https://images.unsplash.com/photo-1600891964599-f61ba0e24092',
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836',
  'https://images.unsplash.com/photo-1519864600265-abb23847ef2c',
  'https://images.unsplash.com/photo-1464306076886-debca5e8a6b0',
];

// Helper to upload all images
const uploadImages = async (files, token) => {
    const urls = [];
    for (const file of files) {
        const url = await uploadImage(file, token);
        urls.push(url);
    }
    return urls;
};

const BasesEditModal = ({ isOpen, onClose, kernelId, onSuccess }) => {
    const { user, userProfile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    
    // Form state
    const [title, setTitle] = useState('');
    const [slotsJson, setSlotsJson] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [context, setContext] = useState('');
    const [techniqueTips, setTechniqueTips] = useState('');
    const [commonMistakes, setCommonMistakes] = useState('');
    const [equipment, setEquipment] = useState('');
    const [difficultyLevel, setDifficultyLevel] = useState('intermediate');
    const [timeRange, setTimeRange] = useState('');
    const [temperatureSettings, setTemperatureSettings] = useState('');
    const [imageUrls, setImageUrls] = useState(['']);
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [dragOver, setDragOver] = useState(false);

    // Load kernel data when modal opens
    useEffect(() => {
        if (isOpen && kernelId) {
            fetchKernel();
        }
    }, [isOpen, kernelId]);

    const fetchKernel = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();
            const kernel = await getBaseById(kernelId, token, true); // includeImages = true
            
            setTitle(kernel.title);
            setSlotsJson(JSON.stringify(kernel.slots || {}, null, 2));
            setImageUrls(kernel.images && kernel.images.length > 0 ? kernel.images : ['']);
            
            // Load context fields
            setContext(kernel.context || '');
            setTechniqueTips(kernel.technique_tips || '');
            setCommonMistakes(kernel.common_mistakes || '');
            setEquipment(kernel.equipment || '');
            setDifficultyLevel(kernel.difficulty_level || 'intermediate');
            setTimeRange(kernel.time_range || '');
            setTemperatureSettings(kernel.temperature_settings || '');
            setImagePreviews(kernel.images && kernel.images.length > 0 ? kernel.images : []);
            setImageFiles([]); // No local files yet
        } catch (err) {
            console.error('Error fetching kernel:', err);
            setError('Failed to load kernel');
        } finally {
            setLoading(false);
        }
    };

    const validateJson = (jsonString) => {
        try {
            JSON.parse(jsonString);
            setJsonError('');
            return true;
        } catch (err) {
            setJsonError(`Invalid JSON: ${err.message}`);
            return false;
        }
    };

    const handleSlotsChange = (value) => {
        setSlotsJson(value);
        validateJson(value);
    };

    const addImageUrl = () => {
        setImageUrls([...imageUrls, '']);
    };

    const removeImageUrl = (index) => {
        if (imageUrls.length > 1) {
            setImageUrls(imageUrls.filter((_, i) => i !== index));
        }
    };

    const updateImageUrl = (index, value) => {
        const newUrls = [...imageUrls];
        newUrls[index] = value;
        setImageUrls(newUrls);
    };

    const handleImageUpload = (files) => {
        const fileArray = Array.from(files);
        setImageFiles(prev => [...prev, ...fileArray]);
        fileArray.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreviews(prev => [...prev, e.target.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleFileInput = (e) => {
        handleImageUpload(e.target.files);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleImageUpload(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const removeImage = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!title.trim()) {
            setError('Title is required');
            return;
        }

        if (!validateJson(slotsJson)) {
            setError('Please fix JSON syntax errors');
            return;
        }

        try {
            setSaving(true);
            setError(null);
            
            const token = await user.getIdToken();
            
            let uploadedImageUrls = [];
            if (imageFiles.length > 0) {
                uploadedImageUrls = await uploadImages(imageFiles, token);
            }
            const validImageUrls = [
                ...uploadedImageUrls,
                ...imagePreviews.filter(url => typeof url === 'string' && url.startsWith('http'))
            ];
            
            const kernelData = {
                title: title.trim(),
                slots: JSON.parse(slotsJson),
                imageUrls: validImageUrls,
                context: context.trim(),
                technique_tips: techniqueTips.trim(),
                common_mistakes: commonMistakes.trim(),
                equipment: equipment.trim(),
                difficulty_level: difficultyLevel,
                time_range: timeRange.trim(),
                temperature_settings: temperatureSettings.trim()
            };

            // Use admin endpoint if user is admin, otherwise use regular endpoint
            if (userProfile?.is_admin) {
                await updateAdminBase(kernelId, kernelData, token);
            } else {
                await updateBase(kernelId, kernelData, token);
            }
            
            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (err) {
            console.error('Error updating kernel:', err);
            setError('Failed to update kernel');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Edit Base"
            size="large"
        >
            <div className="vspace">
                {loading ? (
                    <div className="text-center py-8">
                        <Text>Loading base...</Text>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="p-4 bg-red-50 hairline">
                                <Text className="text-red-900 text-sm">{error}</Text>
                            </div>
                        )}

                        {/* Basic Information */}
                        <div>
                            <Heading level={4} className="mb-4">Basic Information</Heading>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                        TITLE *
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-neutral-900 focus:outline-none focus:border-neutral-600 font-mono text-sm bg-neutral-50"
                                        placeholder="Enter base title..."
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                        TEMPLATE SLOTS (JSON) *
                                    </label>
                                    <textarea
                                        value={slotsJson}
                                        onChange={(e) => handleSlotsChange(e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        rows={6}
                                        placeholder='{"ingredient1": "description", "ingredient2": "description"}'
                                        required
                                    />
                                    {jsonError && (
                                        <Text className="text-red-600 text-sm mt-1">{jsonError}</Text>
                                    )}
                                </div>

                                <div>
                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                        DIFFICULTY LEVEL
                                    </label>
                                    <select
                                        value={difficultyLevel}
                                        onChange={(e) => setDifficultyLevel(e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                    >
                                        <option value="beginner">Beginner</option>
                                        <option value="intermediate">Intermediate</option>
                                        <option value="advanced">Advanced</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Context and Tips */}
                        <div>
                            <Heading level={4} className="mb-4">Context and Tips</Heading>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                        CONTEXT
                                    </label>
                                    <textarea
                                        value={context}
                                        onChange={(e) => setContext(e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        rows={3}
                                        placeholder="Explain the technique, its purpose, and when to use it..."
                                    />
                                </div>

                                <div>
                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                        TECHNIQUE TIPS
                                    </label>
                                    <textarea
                                        value={techniqueTips}
                                        onChange={(e) => setTechniqueTips(e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        rows={3}
                                        placeholder="Expert tips and best practices..."
                                    />
                                </div>

                                <div>
                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                        COMMON MISTAKES
                                    </label>
                                    <textarea
                                        value={commonMistakes}
                                        onChange={(e) => setCommonMistakes(e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        rows={3}
                                        placeholder="Common mistakes and how to avoid them..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Equipment and Settings */}
                        <div>
                            <Heading level={4} className="mb-4">Equipment and Settings</Heading>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                        EQUIPMENT
                                    </label>
                                    <textarea
                                        value={equipment}
                                        onChange={(e) => setEquipment(e.target.value)}
                                        className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        rows={2}
                                        placeholder="Essential tools and equipment needed..."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                            TIME RANGE
                                        </label>
                                        <input
                                            type="text"
                                            value={timeRange}
                                            onChange={(e) => setTimeRange(e.target.value)}
                                            className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                            placeholder="e.g., 15-30 minutes"
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                            TEMPERATURE SETTINGS
                                        </label>
                                        <input
                                            type="text"
                                            value={temperatureSettings}
                                            onChange={(e) => setTemperatureSettings(e.target.value)}
                                            className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                            placeholder="e.g., Medium-high heat"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Images */}
                        <div>
                            <Heading level={4} className="mb-4">Template Images</Heading>
                            
                            <div className="space-y-6">
                                {/* Upload Area */}
                                <div 
                                    className={`hairline bg-neutral-50 p-8 text-center cursor-pointer transition-colors ${dragOver ? 'bg-neutral-100' : 'hover:bg-neutral-100'}`}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onClick={() => document.getElementById('kernel-image-input').click()}
                                >
                                    <div className="text-4xl mb-4">📷</div>
                                    <Text className="mb-2">
                                        <strong>Click to upload</strong> or drag and drop images here
                                    </Text>
                                    <Text size="sm" className="opacity-60">
                                        PNG, JPG, GIF up to 10MB each
                                    </Text>
                                    <input
                                        id="kernel-image-input"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleFileInput}
                                        className="hidden"
                                    />
                                </div>
                                {/* Image Previews */}
                                {imagePreviews.length > 0 && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {imagePreviews.map((preview, index) => (
                                            <div key={index} className="relative hairline bg-neutral-50">
                                                <img 
                                                    src={preview} 
                                                    alt={`Preview ${index + 1}`} 
                                                    className="w-full h-32 object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(index)}
                                                    className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-neutral-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={saving}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors disabled:opacity-50"
                            >
                                CANCEL
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50"
                            >
                                {saving ? 'SAVING...' : 'UPDATE KERNEL'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Modal>
    );
};

export default BasesEditModal; 
